import { type KeyboardEvent, useRef } from 'react'
import { cn } from '@/shared/lib'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'

interface TagInputProps {
  id?: string
  value: string[]
  onChange: (next: string[]) => void
  /** Текст в инпуте — контролируется родителем (он же грузит подсказки по нему). */
  query: string
  onQueryChange: (query: string) => void
  suggestions?: string[]
  placeholder?: string
  maxTags?: number
  disabled?: boolean
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

function normalize(tag: string) {
  return tag.trim().toLowerCase()
}

// Dumb-поле тегов: выбранные чипы с удалением + инпут, а подсказки — рядом
// кликабельных чипов под полем (не выпадающий список: он перекрывает контент и
// требует прицеливания). Enter/запятая добавляет введённое, Backspace на пустом
// инпуте снимает последний тег. Данные (value/query/suggestions) полностью у
// родителя — компонент без побочек.
export function TagInput({
  id,
  value,
  onChange,
  query,
  onQueryChange,
  suggestions = [],
  placeholder,
  maxTags,
  disabled,
  ...aria
}: TagInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const atLimit = maxTags !== undefined && value.length >= maxTags
  const visible = suggestions
    .filter((s) => !value.some((t) => normalize(t) === normalize(s)))
    .slice(0, 8)

  function add(raw: string) {
    const tag = raw.trim()
    if (!tag || atLimit) return
    if (value.some((t) => normalize(t) === normalize(tag))) {
      onQueryChange('')
      return
    }
    onChange([...value, tag])
    onQueryChange('')
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      // Enter внутри формы не должен сабмитить, пока пользователь набирает тег.
      if (query.trim() !== '' || e.key === ',') e.preventDefault()
      add(query)
      return
    }
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      remove(value.length - 1)
    }
  }

  return (
    <div>
      {/* label-обёртка: клик по любому месту контрола фокусирует инпут нативно */}
      <label
        className={cn(
          'flex w-full cursor-text flex-wrap items-center gap-2 rounded-control border border-border-default bg-bg-sunken px-4 py-3 transition-colors focus-within:border-border-accent',
          disabled && 'opacity-50',
        )}
      >
        {value.map((tag, i) => (
          <Chip key={tag} tone="info" className="normal-case">
            {tag}
            <button
              type="button"
              aria-label={`Убрать тег ${tag}`}
              className="text-text-tertiary transition-colors hover:text-text-primary"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                remove(i)
              }}
            >
              <Icon name="close" size={12} />
            </button>
          </Chip>
        ))}
        <input
          ref={inputRef}
          id={id}
          {...aria}
          className="min-w-28 flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary focus:outline-none"
          value={query}
          disabled={disabled || atLimit}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Набранный, но не подтверждённый Enter'ом текст — тоже тег: иначе
            // «ввёл и сразу нажал Создать» молча теряет тег.
            add(query)
          }}
        />
      </label>
      {!atLimit && visible.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="label text-text-tertiary">
            {query.trim() === '' ? 'Популярные' : 'Совпадения'}
          </span>
          {visible.map((s) => (
            <button
              key={s}
              type="button"
              disabled={disabled}
              aria-label={`Добавить тег ${s}`}
              // mousedown раньше blur инпута — иначе набранный текст успеет уйти
              // в теги и подсказка добавится второй.
              onMouseDown={(e) => {
                e.preventDefault()
                add(s)
                inputRef.current?.focus()
              }}
              className="rounded-control transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent disabled:opacity-40"
            >
              <Chip variant="soft" className="normal-case">
                <Icon name="plus" size={11} className="text-text-tertiary" />
                {s}
              </Chip>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
